import { useEffect, useRef } from "react";
import { useFeatureFlag } from "@/lib/FeatureFlagContext";
import { FeatureFlagZone } from "@/components/FeatureFlagZone";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        params: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const SITE_KEY = (import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY as string | undefined) ?? "";

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  className?: string;
}

/**
 * Renders a Cloudflare Turnstile CAPTCHA widget.
 *
 * Completely hidden when the `turnstile_enabled` feature flag is off OR when
 * VITE_CLOUDFLARE_TURNSTILE_SITE_KEY is not configured. In that case
 * `onVerify` is called immediately with an empty string so callers don't
 * need to gate their submit logic on the flag themselves.
 *
 * When active the widget loads the Cloudflare script once, renders in dark
 * mode, and calls `onVerify(token)` after a successful challenge.
 */
export function TurnstileWidget({ onVerify, onExpire, className }: TurnstileWidgetProps) {
  const turnstileEnabled = useFeatureFlag("turnstile_enabled");
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const isActive = turnstileEnabled && !!SITE_KEY;

  useEffect(() => {
    if (!isActive) {
      onVerify("");
      return;
    }

    const renderWidget = () => {
      if (!containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: onVerify,
        "expired-callback": onExpire ?? (() => onVerify("")),
        theme: "dark",
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      window.onTurnstileLoad = renderWidget;
      const existing = document.querySelector(
        'script[src*="challenges.cloudflare.com/turnstile"]',
      );
      if (!existing) {
        const script = document.createElement("script");
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <FeatureFlagZone flagKey="turnstile_enabled" label="Turnstile CAPTCHA">
      <div ref={containerRef} className={className} data-testid="turnstile-widget" />
    </FeatureFlagZone>
  );
}
